import { ConfiguredCarCarCode, useQuery } from '@oneaudi/onegraph-client';
import { useAtom, useAtomValue } from 'jotai';
import md5 from 'md5';
import { useEffect, useMemo, useRef } from 'react';
import { useAppState } from '~/app/contexts/app-context';
import { useMarketConfig } from '~/app/market-config';
import { hyphenateModelLine } from '~/app/market-config/hyphenate-carline';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import * as feldm from '~/app/tracking/feld-m-types';
import { mapTrackingIncentives } from '~/app/tracking/map-tracking-incentives';
import {
	ConfiguredCarTrackingDocument,
	ConfiguredCarTrackingQuery,
	FinancingInput,
} from '~/graphql/__generated__/graphql';
import { useConfigurator } from '~/graphql/hooks/use-configurator';

import { NgcCarIdentifier } from '~/app/utils/graphql-sub-types';
import { state } from '~/storage';
import { debounce } from '~/utils/debounce';

function str(s?: string | number | null) {
	return s?.toString() || '';
}

function bool(b?: boolean) {
	return b ? 'true' : 'false';
}

export type ItemTrackingData =
	| (NonNullable<ConfiguredCarTrackingQuery['configuredCar']['catalog']>['features']['data'][0] & {
			totalPrice?: { value: number };
	  })
	| (NonNullable<
			NonNullable<ConfiguredCarTrackingQuery['configuredCar']['catalog']>['packagesDetails']['data']
	  >[0] & {
			totalPrice?: { value: number };
			content?: ItemTrackingData[];
	  });

export function createItem(item: ItemTrackingData, manualOrAutomatic?: string): feldm.CarPart | feldm.Package {
	return {
		productInfo: {
			productID: item?.pr3 || item?.pr7 || '',
			productName: item.name || '',
			manufacturer: 'Audi',
		},
		category: {
			primaryCategory: item?.featureType || '',
			productType: item.__typename === 'ConfiguredCarPackage' ? 'package' : 'car part',
		},
		price: {
			price: str(Math.abs(item.price?.value || item.totalPrice?.value || 0)),
		},
		attributes: {
			prString7: item?.pr7 || '',
			familyID: str(item?.family?.id) || '',
			familyName: str(item?.family?.name) || '',
			groupID: str(item?.group?.id) || '',
			groupName: str(item?.group?.name) || '',
			isPreSelected: '',
			isStandardEquipment: bool(item?.status?.standard),
			selectionType: manualOrAutomatic || item?.status.selected ? 'manual' : 'automatic',
		},
		...(item.__typename === 'ConfiguredCarPackage' && (item.content || item.features?.data)
			? {
					carParts:
						item.features?.data.map((feature) => createItem(feature)) || [
							item.content?.map((feature) => createItem(feature)),
						] ||
						[],
				}
			: {}),
	};
}

function mapTrackingData(
	data: ConfiguredCarTrackingQuery,
	entryType: 'audicode' | 'pr string' | 'default',
	incentives: feldm.ProductDataConfiguredVehicle['incentives'],
	financingInput?: FinancingInput,
	audiCodeData?: ConfiguredCarCarCode,
	additionalData?: {
		configurationStartTime?: string;
	},
): feldm.ProductDataConfiguredVehicle {
	const { configuredCar } = data;

	return {
		productInfo: {
			productID: str(configuredCar.carline?.id),
			productName: str(configuredCar.carline?.name),
			configurationID: configuredCar.id,
			configurationHash: md5(JSON.stringify(configuredCar.id)),
			manufacturer: 'Audi',
		},
		category: {
			primaryCategory: str(hyphenateModelLine(configuredCar.carline!.id)),
			subCategory1: str(configuredCar.carline?.id),
			productType: 'configured car',
		},
		price: {
			totalPrice: str(configuredCar.prices?.priceParts.find((p) => p.type === 'total')?.price.value),
			modelPrice: str(configuredCar.prices?.priceParts.find((p) => p.type === 'model')?.price.value),
			optionsPrice: str(configuredCar.prices?.priceParts.find((p) => p.type === 'options')?.price.value),
			rateTotalPrice:
				configuredCar.prices?.monthlyRate?.__typename === 'MonthlyRate'
					? str(configuredCar.prices?.monthlyRate?.rawValue)
					: '',
			currency: str(
				configuredCar.prices?.priceParts.find((p) => p.type === 'total')?.price.currencyDetails?.code,
			),
		},
		...(financingInput && {
			financing: {
				financingName: financingInput?.product,
				mileage: financingInput?.productParameters?.find((p) => p?.key === 'Mileage')?.value || '',
				duration: financingInput?.productParameters?.find((p) => p?.key === 'Duration')?.value || '',
				downPayment: financingInput?.productParameters?.find((p) => p?.key === 'DownPayment')?.value || '',
			},
		}),

		attributes: {
			modelLine: str(hyphenateModelLine(configuredCar.carline!.id)),
			modelYear: str(configuredCar.model?.id?.year),
			bodyType: '', // TODO: What is body type?
			audiCode: str(audiCodeData?.id),
			isSaved: 'false', // TODO: Not possible yet to store a config in MyAudi?
			configurationStartTime: str(additionalData?.configurationStartTime),
			entryType,
			engine: str(configuredCar.catalog?.models?.data?.find((m) => m?.status?.selected)?.engineName),
		},
		carParts: configuredCar.catalog?.features.data.map((feature) => createItem(feature)) || [],
		packages:
			(configuredCar.catalog?.packagesDetails.data?.map((feature) => createItem(feature)) as feldm.Package[]) ||
			[],
		incentives,
	};
}

export function useConfiguredVehicle() {
	const {
		config: { financingInput, enableFinancingServices, enablePartsRateCalculation },
	} = useMarketConfig();
	const { carIdentifier: ci, isLoading, catalog, didConfigChange: configuratorDidConfigChange } = useConfigurator();
	const prevCarIdentifierString = useRef<string | null>(null);
	const isInitialized = useRef(false);
	const currentView = useAtomValue(state.currentView);
	const additionalData = useRef<{ configurationStartTime?: string }>({});
	const didConfigChange = useRef(false);
	const prevFinancingRef = useRef<string | undefined>(undefined);
	const { audiCode } = useAppState();
	const entryType = useAtomValue(state.entryType);
	const [, setHasConfigChanged] = useAtom(state.hasConfigChanged);
	const [, setHasFinancingChanged] = useAtom(state.hasFinancingChanged);

	const urlWithHash = () => {
		if (!window.location.hash) return window.location.href + '#' + currentView;
		return document.URL;
	};

	useEffect(() => {
		if (catalog && !isLoading && !isInitialized.current) {
			isInitialized.current = true;
		}
	}, [JSON.stringify(catalog), isLoading]);

	useEffect(() => {
		if (!isLoading && isInitialized.current) {
			// Debounce to trigger event after currentView is changing
			const trackPageLoad = debounce(() => {
				appTrackingService.pageLoad(currentView, urlWithHash());
			}, 150);
			trackPageLoad();
		}
	}, [currentView, isLoading]);

	const carIdentifier: NgcCarIdentifier = useMemo(() => {
		return {
			brand: ci.brand,
			country: ci.country,
			language: ci.language,
			model: {
				code: ci.model.code,
				version: ci.model.version,
				year: ci.model.year,
				extensions: ci.model.extensions,
			},
			equipmentOptions: ci.equipmentOptions,
			exteriorColor: ci.exteriorColor,
			interiorColor: ci.interiorColor,
		};
	}, [ci.brand, ci.country, ci.equipmentOptions, ci.exteriorColor, ci.interiorColor, ci.language, ci.model]);

	const { data: trackingData, loading: trackingDataLoading } = useQuery(ConfiguredCarTrackingDocument, {
		variables: {
			configuredCarIdentifier: ci,
			financingInput,
			featuresFilterInput: {
				filterByFeatureState: ['selected'],
			},
			financingEnabled: enablePartsRateCalculation && enableFinancingServices,
		},
		returnPartialData: true,
		fetchPolicy: 'no-cache',
	});

	const incentives = useMemo(() => {
		if (!trackingData || !trackingData.configuredCar.incentives) return [];

		// Find the incentive for the configured engine
		const configuredEngineIncentives =
			trackingData.configuredCar.carlineStructureCarline?.trimlines
				.flatMap((trimline) => trimline.models)
				.find((model) => model.modelId.code === trackingData.configuredCar.model?.id?.code)?.incentives || [];

		// Match them to the incentives on the configuredCar level to get the status field
		const engineIncentivesIds = new Set(configuredEngineIncentives.map((item) => item && item.id));
		const matchingIncentives = trackingData.configuredCar.incentives.filter((item) =>
			engineIncentivesIds.has(item.id),
		);

		// Map matched incentives to the feldM format
		const mappedIncentives = mapTrackingIncentives(matchingIncentives);

		return mappedIncentives;
	}, [trackingData]);

	useEffect(() => {
		if (trackingData) {
			const configuredVehicle = mapTrackingData(
				trackingData,
				entryType,
				incentives,
				financingInput || undefined,
				audiCode?.data,
				additionalData.current,
			);

			appTrackingService.setProductData({ configuredVehicle: [configuredVehicle] });

			// Check if financing input has changed
			const newFinancialInput = financingInput ? JSON.stringify(financingInput) : undefined;
			if (prevFinancingRef?.current !== newFinancialInput) {
				setHasFinancingChanged(true);
			}
			prevFinancingRef.current = newFinancialInput;
		}
	}, [
		JSON.stringify(audiCode?.data),
		JSON.stringify(trackingData),
		entryType,
		JSON.stringify(catalog),
		JSON.stringify(incentives),
		JSON.stringify(financingInput),
	]);

	useEffect(() => {
		if (!trackingDataLoading) {
			appTrackingService.configurationChange();
			// this is to update the hasConfigChanged atom when configuration changes and this effect is run on every configuration change
			setHasConfigChanged(didConfigChange.current);
		}
	}, [trackingDataLoading, configuratorDidConfigChange, setHasConfigChanged]);

	useEffect(() => {
		if (!trackingDataLoading && didConfigChange.current) {
			appTrackingService.configurationStartAfter();

			didConfigChange.current = false;
		}
	}, [trackingDataLoading]);

	useEffect(() => {
		const carIdentifierString = JSON.stringify(carIdentifier);
		didConfigChange.current =
			!!prevCarIdentifierString.current &&
			carIdentifierString !== prevCarIdentifierString.current &&
			!!configuratorDidConfigChange;
		prevCarIdentifierString.current = carIdentifierString;
		if (isInitialized.current && didConfigChange.current) {
			additionalData.current.configurationStartTime = Date.now().toString();
			appTrackingService.configurationStartBefore();
		}
	}, [JSON.stringify(carIdentifier), configuratorDidConfigChange, trackingDataLoading]);
}
